import { ComponentType, createContext } from "preact"
import { PageType, QueryProvider, SearchQuery } from "./api"
import { tagging } from "./api/nosto"
import { MapperOptions } from "./urlEncoding/types"
import { CssSelector } from "./helpers/resolveCssSelector"
import { HitDecorator } from "@nosto/search-js"
import { AutocompleteOptions } from "./autocomplete"
import { SerpConfig } from "@nosto/search-js/preact/serp"

export const defaultConfig = {
    autocompleteMinLength: 2,
    historyEnabled: true,
    historySize: 5,
    serpUrlMapping: {
        name: "name",
        query: "query",
        "product.personalizationBoost": "aff",
        segments: "segments",
        customRules: "customRules",
        explain: "explain",
        time: "time",
        products: "products",
        keywords: "keywords"
    },
    defaultCurrency: "EUR",
    serpPathRedirect: false,
    categoryId: () => tagging.categoryId(),
    categoryPath: () => tagging.categoryPath(),
    variationId: () => tagging.variation(),
    brand: () => tagging.brand(),
    // FIXME this default implementation relies on a deprecated config property
    categoryQuery(this: Config) {
        return {
            products: {
                categoryId: this.categoryId!(),
                categoryPath: this.categoryPath!(),
                size: this.serpSize,
                from: 0
            }
        }
    },
    isCategoryPage: () => tagging.pageType() === "category",
    compressUrlParameters: false,
    debounceDelay: 300,
    persistentSearchCache: false,
    memoryCache: false,
    preservePageScroll: false,
    queryModifications: query => query,
    hitDecorators: [],
    retries: 3,
    retryInterval: 500,
    speechToTextEnabled: false
} satisfies Partial<Config>

/**
 * User provided configuration
 */
export interface Config {
    /**
     * CSS selector for each input element to bind search events like input change and form submit.
     */
    inputCssSelector?: string

    /**
     * CSS selector for autocomplete dropdown render.
     * Leave undefined for default use (After input element)
     */
    dropdownCssSelector?: CssSelector

    /**
     * CSS selector for search page rendering.
     */
    contentCssSelector?: string

    /**
     * CSS selector for category content rendering
     */
    categoryCssSelector?: string

    /**
     * Determine if current page is category
     * Example: return location.pathname.includes('category')
     */
    isCategoryPage?: () => boolean

    /**
     * Default category page query parameters. Supplied as an object or a function that returning search query
     * Functional version can be use for building complex category queries. It's more flexible and provides access to other config parameters.
     * @example
     * as object:
     *
     * categoryQuery: {
     *   products: {
     *      facets: ["*"],
     *      fields: ["variantId", "availability", ....]
     *      ...
     *   }
     * }
     *
     * @example
     * as function:
     *
     * categoryQuery() {
     *   return {
     *      products: {
     *         facets: ["*"],
     *         fields: ["variantId", "availability", ....]
     *         variationId: this.variationId()
     *         ...
     *      }
     *   }
     * }
     */
    categoryQuery?: QueryProvider

    /**
     * returns current category id
     * @deprecated no need to provide this as the builtin logic is usually more reliable
     */
    categoryId?: () => string | undefined
    /**
     * returns current category path
     * @deprecated no need to provide this as the builtin logic is usually more reliable
     */
    categoryPath?: () => string | undefined
    /**
     * return current variation id, can be supplying for supplying multi-currency and B2B variations
     * @deprecated no need to provide this as the builtin logic is usually more reliable
     */
    variationId?: () => string | undefined
    /**
     * returns current brand
     */
    brand?: () => string | undefined
    /**
     * Category root component
     */
    categoryComponent?: ComponentType
    /**
     * CSS selector for form to unbind it.
     */
    formCssSelector: string
    /**
     * Form unbind delay after how long it should delay, default is unbinding without delay ms
     */
    formUnbindDelay?: number
    /**
     * Merchant ID.
     */
    merchant: string
    /**
     * Merchant's default Currency.
     */
    defaultCurrency: string

    /**
     * Default search page query parameters. Supplied as an object or a function that returning search query
     * Functional version can be use for building complex search queries. It's more flexible and provides access to other config parameters.
     * @example
     * as object:
     *
     * serpQuery: {
     *   products: {
     *      facets: ["*"],
     *      fields: ["variantId", "availability", ....]
     *      ...
     *   }
     * }
     *
     * @example
     * as function:
     *
     * serpQuery() {
     *   return {
     *      products: {
     *         facets: ["*"],
     *         fields: ["variantId", "availability", ....]
     *         variationId: this.variationId()
     *         ...
     *      }
     *   }
     * }
     */
    serpQuery?: QueryProvider

    /**
     * Preact search page main component.
     */
    serpComponent?: ComponentType

    /**
     * Search page path, renders in any page if not specified
     */
    serpPath?: string

    /**
     * Indicates if search should redirect to search page or just rewrite history
     *
     * In case a function is provided, it will be called with the query object and autocomplete options.
     */
    serpPathRedirect?: boolean | ((query: SearchQuery, options: AutocompleteOptions | undefined) => void)

    /**
     * Preact autocomplete main component
     */
    autocompleteComponent?: ComponentType

    /**
     * Default autocomplete page query parameters. Supplied as an object or a function that returning search query
     * Functional version can be use for building complex autocomplete queries. It's more flexible and provides access to other config parameters.
     * @example
     * as object:
     *
     * autocompleteQuery: {
     *   products: {
     *      facets: ["*"],
     *      fields: ["variantId", "availability", ....]
     *      ...
     *   }
     * }
     *
     * @example
     * as function:
     *
     * autocompleteQuery() {
     *   return {
     *      products: {
     *         facets: ["*"],
     *         fields: ["variantId", "availability", ....]
     *         variationId: this.variationId()
     *         ...
     *      }
     *   }
     * }
     */
    autocompleteQuery?: QueryProvider

    /**
     * Minimal input length to render autocomplete
     *
     * @default 2
     */
    autocompleteMinLength?: number

    /**
     * Front end URL both ways mapping, to convert URL param values and keep the request keys
     * {
     *    query: 'q' - will map query param to appear as q in URL
     *    product.filters: 'ff' - will map to ff.0.field.0: 'brand' and so on..
     *    product.size: 'size' - will map to size: 24
     * }
     */
    serpUrlMapping?: Record<string, string>

    /**
     * @hidden Custom URL parameters encodings and decoding. Used for backward compatibility
     */
    customUrlMappings?: {
        encode(query: unknown, options: MapperOptions): unknown
        decode(query: unknown, options: MapperOptions): unknown
    }[]

    /**
     * Indicates if URL parameters should be compressed.
     * If enabled, default range separator is '~'.
     *
     * @example
     * as boolean
     *
     * compressUrlParameters: true
     *
     * @example
     * as object
     * compressUrlParameters: {
     *   filterRangeSeparator: "~"
     * }
     *
     * @default false
     */
    compressUrlParameters: boolean | Partial<MapperOptions>

    /**
     * Indicates if history should be enabled
     *
     * @default true
     */
    historyEnabled?: boolean

    /**
     * History component
     */
    historyComponent?: ComponentType

    /**
     * Speech to text component
     */
    speechToTextComponent?: ComponentType<{
        /**
         * Callback to be called when speech to text input is submitted
         */
        onSubmit: (value: string) => void
    }>

    /**
     * Indicates if speech to text should be enabled
     *
     * @default false
     */
    speechToTextEnabled?: boolean

    /**
     * History size
     *
     * @default 5
     */
    historySize?: number

    /**
     *  Number of search results to be displayed on the SERP
     *
     * @hidden is provided under the same name in default-design defaultConfig
     * @deprecated use default design's defaultConfig instead
     */
    serpSize?: number

    /**
     * Enabled fallback for serp & category pages when service is unavailable
     * By default, it will restore the native content.
     *
     * If set to 'legacy', it will force a page reload and disable the search for the current session.
     *
     * @default false
     */
    fallback?: boolean | "legacy"

    /**
     * Number of retries for search request.
     * Retries won't be applied for autocomplete requests.
     *
     * @default 3
     */
    retries?: number

    /**
     * Interval between retries in milliseconds
     *
     * @default 500
     */
    retryInterval?: number

    /**
     * Enable search fallback when service is unavailable (overwrites global `fallback` option).
     *
     * By default it will redirect to original page by disabling new page.
     * If custom function is provided, it will be called with query object.
     *
     * It's important that this path does not have same path as search page.
     *
     * @example
     * ```
     * serpFallback: (query) => {
     *  location.replace(`/search?q=${query.query}`)
     * }
     * ```
     */
    serpFallback?: (query: SearchQuery) => void

    /**
     * Enable category fallback when service is unavailable (overwrites global `fallback` option).
     *
     * By default it will redirect to original page by disabling new page.
     * If custom function is provided, it will be called with query object.
     *
     * It's important that this path does not have same path as search page.
     *
     * @example
     * ```
     * categoryFallback: (query) => {
     *  location.replace(`/categories/${query.products.categoryId}`)
     * }
     * ```
     */
    categoryFallback?: (query: SearchQuery) => void

    /**
     * Debounce delay for search input
     */
    debounceDelay?: number

    /**
     * Delay serp page rendering given until given async function resolves
     * Default function waitForElement can be imported from "@nosto/preact"
     *
     * @example To wait for Magento script to load
     * ```
     * import { waitForElement } from "@nosto/preact"
     *
     * init({
     * ...
     * serpRenderDelay: () => {
     *  return waitForElement({
     *      selector: 'script[type="text/x-magento-init"]',
     *  })
     * }
     * ```
     */
    serpRenderDelay?: () => Promise<void>

    /**
     * Enable persistent caching for search results.
     *
     * If enabled, the search results will be restored from cache when returning to the search page,
     * from example, from a product page.
     *
     * The cache is stored in the browser's session storage.
     */
    persistentSearchCache?: boolean

    /**
     * Enable in-memory caching for autocomplete results.
     *
     * If enabled, the autocomplete results will be cached in memory,
     * and the same results will be returned for the same query without making a new request.
     */
    memoryCache?: boolean

    /**
     * Preserve page scroll position when navigating back to search page.
     *
     * If enabled, it's highly recommended to also enabled `persistentSearchCache` for best user experience.
     */
    preservePageScroll?: boolean

    /**
     * Custom function, when provided, can be used to modify search query before sending it to the server.
     * A default implementation is provided in the config that returns the query without any modification.
     * Note: The query parameter itself should not be touched, but instead a new object should be returned in case modifications are needed.
     * @param query SearchQuery
     * @param pageType PageType | undefined
     * @default queryModifications: query => query
     * @example
     * ```
     * (query, pageType) => {
     *   return {
     *     ...query,
     *     products: {
     *       ...query.products,
     *       filter: [
     *         ...(query.products?.filter ?? []),
     *         { field: "availability", value: ["InStock"] }
     *       ]
     *     }
     *   }
     * }
     * ```
     */
    queryModifications?: (query: SearchQuery, pageType: PageType | undefined) => SearchQuery

    /**
     * Hit decorators are used to modify the products hits before they are rendered.
     */
    hitDecorators?: HitDecorator[]
}

type MakeRequired<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>

/**
 * Full configuration used internally in the Search application
 * @interface
 */
export type FullConfig = MakeRequired<Config, keyof typeof defaultConfig> &
    Pick<SerpConfig, "onBeforeSearch" | "onSearchError" | "search">

// @ts-expect-error type mismatch
export const ConfigContext = createContext<FullConfig>(defaultConfig)

declare global {
    interface Window {
        nostoTemplatesFileResolver?: (path: string) => string
    }
}

/**
 * When importing static resources (e.g. images), import will return relative url.
 * To use that url to load image, you need to resolve that to absolute url using `resolveStaticFile`.
 *
 * Uses `window.nostoTemplatesFileResolver` callback to format url.
 *
 * @example
 * ```
 * import url from './images/image.jpeg'
 *
 * console.log(resolveStaticFile(url))
 * ```
 */
export function resolveStaticFile(path: string): string {
    const relativePath = path[0] === "/" ? path.slice(1) : path

    if (window.nostoTemplatesFileResolver) {
        return window.nostoTemplatesFileResolver(relativePath)
    }
    const src = document.currentScript instanceof HTMLScriptElement ? document.currentScript.src : ""
    return new URL(relativePath, src).toString()
}

export function resolveQuery(config: Partial<Config>, queryField: "serpQuery" | "categoryQuery" | "autocompleteQuery") {
    const field = config[queryField]
    if (typeof field === "function") {
        return field.bind(config)()
    }
    return field
}
